import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import apiV2 from "../utils/apiV2";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent } from "./ui/dialog";
import { 
  ChevronLeft, ChevronRight, Calendar,
  AlertCircle, ArrowRight, Phone, Mail
} from "lucide-react";
import TabPageLayout from './TabPageLayout';
import EmptyState from './EmptyState';
import BookingCard from './BookingCard';

// Fetch daily schedule
const fetchDailySchedule = async (dateStr) => {
  const params = dateStr ? `?date_str=${dateStr}` : '';
  const response = await apiV2.get(`/schedule/daily${params}`);
  return response.data;
};

// Format date for API (YYYY-MM-DD)
const formatDateForApi = (date) => {
  return date.toISOString().split('T')[0];
};

// Get today's date (no time component)
const getToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

// Extract hour from time string (e.g., "9:30 AM" -> "9 AM")
const getHourSlot = (timeStr) => {
  if (!timeStr) return "Unknown";
  const match = timeStr.match(/^(\d+):/);
  if (!match) return timeStr;
  const hour = parseInt(match[1]);
  const isPM = timeStr.toUpperCase().includes('PM');
  return `${hour} ${isPM ? 'PM' : 'AM'}`;
};

// Group bookings by hour slot
const groupByHour = (bookings) => {
  const groups = {};
  bookings.forEach(booking => {
    const slot = getHourSlot(booking.start_time);
    if (!groups[slot]) {
      groups[slot] = [];
    }
    groups[slot].push(booking);
  });
  return groups;
};

// Rotating colors for time slot left borders
const SLOT_COLORS = [
  '#3B82F6', // blue
  '#8B5CF6', // purple
  '#14B8A6', // teal
  '#F97316', // orange
  '#EC4899', // pink
  '#10B981', // emerald
  '#6366F1', // indigo
  '#F59E0B', // amber
];

const ScheduleTab = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(getToday());
  const [contactModal, setContactModal] = useState({ open: false, type: null, value: null, name: null, booking: null });
  const [resendingConfirmation, setResendingConfirmation] = useState(false);

  const dateStr = formatDateForApi(currentDate);
  
  const { data, error, isLoading, mutate } = useSWR(
    ['daily-schedule', dateStr],
    () => fetchDailySchedule(dateStr),
    { 
      revalidateOnFocus: true,
      dedupingInterval: 30000
    }
  );

  const bookings = data?.bookings || [];
  const nextBookingDate = data?.next_booking_date;
  const groupedBookings = groupByHour(bookings);
  const hourSlots = Object.keys(groupedBookings);

  // Navigate days
  const goToPreviousDay = () => {
    const prev = new Date(currentDate);
    prev.setDate(prev.getDate() - 1);
    setCurrentDate(prev);
  };

  const goToNextDay = () => {
    const next = new Date(currentDate);
    next.setDate(next.getDate() + 1);
    setCurrentDate(next);
  };

  const goToToday = () => {
    setCurrentDate(getToday());
  };

  const jumpToNextBooking = () => {
    if (nextBookingDate) {
      setCurrentDate(new Date(nextBookingDate));
    }
  };

  // Check if viewing today
  const isToday = formatDateForApi(currentDate) === formatDateForApi(getToday());

  // Check if an hour slot has passed (only relevant for today)
  const isHourPassed = (hourSlot) => {
    if (!isToday) return false;
    try {
      const match = hourSlot.match(/^(\d+)\s*(AM|PM)$/i);
      if (!match) return false;
      let hour = parseInt(match[1]);
      const isPM = match[2].toUpperCase() === 'PM';
      if (isPM && hour !== 12) hour += 12;
      if (!isPM && hour === 12) hour = 0;
      return new Date().getHours() > hour;
    } catch {
      return false;
    }
  };

  // Handle contact click - now includes booking for resend feature
  const handleContact = ({ type, value, name, booking }) => {
    setContactModal({ open: true, type, value, name, booking });
  };

  // Handle resending confirmation
  const handleResendConfirmation = async (resendType) => {
    if (!contactModal.booking) {
      console.error('No booking in modal');
      toast.error('No booking selected');
      return;
    }
    
    const orgId = contactModal.booking.organization_id || contactModal.booking.organizationId;
    const bookingId = contactModal.booking.id;
    
    if (!orgId) {
      console.error('No organization_id on booking');
      toast.error('Booking missing organization ID');
      return;
    }
    
    setResendingConfirmation(true);
    try {
      const url = `/organizations/${orgId}/bookings/${bookingId}/resend-confirmation`;
      
      const response = await apiV2.post(url, { type: resendType });
      
      if (response.data.success) {
        toast.success(`Confirmation ${resendType === 'email' ? 'email' : 'SMS'} sent!`);
        setContactModal({ open: false, type: null, value: null, name: null, booking: null });
      } else {
        toast.error(response.data.message || 'Failed to resend');
      }
    } catch (err) {
      console.error('Resend error:', err);
      toast.error(err.response?.data?.detail || 'Failed to resend confirmation');
    } finally {
      setResendingConfirmation(false);
    }
  };

  // Handle edit booking
  const handleEdit = (booking) => {
    // Navigate to schedule tab with edit booking param
    // Uses URL params so dashboard can pick up the tab change
    navigate(`?tab=schedule&editBookingId=${booking.id}`);
  };

  // Error state
  if (error) {
    return (
      <TabPageLayout title="Schedule" subtitle="Daily booking schedule">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Failed to load schedule</h3>
          <p className="text-sm text-gray-500 mb-4">Please check your connection and try again.</p>
          <Button onClick={() => mutate()} className="bg-blue-600 hover:bg-blue-700 text-white h-10">
            Try Again
          </Button>
        </div>
      </TabPageLayout>
    );
  }

  return (
    <TabPageLayout
      title="Schedule"
      subtitle="Daily booking schedule"
      loading={isLoading}
      actions={
        <div className="flex items-center gap-2">
          {/* Today Button */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={goToToday}
            className="h-10 px-4 bg-white shadow-[0_8px_24px_rgba(0,0,0,0.04)] hover:bg-gray-50 text-gray-700"
          >
            Today
          </Button>

          {/* Date Navigator */}
          <div className="bg-white rounded-lg flex items-center shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
            <Button
              variant="ghost"
              size="sm"
              onClick={goToPreviousDay}
              className="h-10 w-10 p-0 rounded-r-none hover:bg-gray-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="h-10 px-2 sm:px-4 flex items-center text-sm font-medium text-gray-900 min-w-[120px] sm:min-w-[150px] justify-center">
              {data?.date_display || 'Loading...'}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={goToNextDay}
              className="h-10 w-10 p-0 rounded-l-none hover:bg-gray-50"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      }
    >
      {/* Contact Modal - Using Dialog for proper focus management */}
      <Dialog open={contactModal.open} onOpenChange={(open) => !open && setContactModal({ open: false, type: null, value: null, name: null, booking: null })}>
        <DialogContent className="w-72 p-0 rounded-2xl border-0 shadow-xl">
          <div className="py-8 px-6">
            <div className="text-center">
              <div className={`w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center ${
                contactModal.type === 'phone' ? 'bg-green-50' : 'bg-blue-50'
              }`}>
                {contactModal.type === 'phone' ? (
                  <Phone className="w-6 h-6 text-green-500" strokeWidth={1.5} />
                ) : (
                  <Mail className="w-6 h-6 text-blue-500" strokeWidth={1.5} />
                )}
              </div>
              <p className="text-sm text-gray-400 font-light mb-1">{contactModal.name}</p>
              <p className="text-lg font-medium text-gray-900 mb-6 select-all tracking-tight">
                {contactModal.value}
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  className={`w-full h-11 text-white font-medium ${
                    contactModal.type === 'phone' 
                      ? 'bg-green-500 hover:bg-green-600' 
                      : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                  onClick={() => {
                    if (contactModal.type === 'phone') {
                      window.location.href = `tel:${contactModal.value}`;
                    } else {
                      window.location.href = `mailto:${contactModal.value}`;
                    }
                  }}
                >
                  {contactModal.type === 'phone' ? 'Call' : 'Send Email'}
                </Button>
                
                {/* Resend Confirmation Button */}
                {contactModal.booking && (
                  <Button
                    variant="outline"
                    className="w-full h-11 font-normal border-gray-200 hover:bg-gray-50"
                    disabled={resendingConfirmation}
                    onClick={() => handleResendConfirmation(contactModal.type === 'phone' ? 'sms' : 'email')}
                  >
                    {resendingConfirmation ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></span>
                        Sending...
                      </span>
                    ) : (
                      `Resend Booking ${contactModal.type === 'phone' ? 'SMS' : 'Email'}`
                    )}
                  </Button>
                )}
                
                <Button
                  variant="ghost"
                  className="w-full h-11 text-gray-500 font-normal hover:bg-gray-50"
                  onClick={() => {
                    navigator.clipboard.writeText(contactModal.value);
                    toast.success('Copied to clipboard');
                  }}
                >
                  Copy to Clipboard
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div data-testid="schedule-tab" className="space-y-6">
        {/* Empty State */}
        {!isLoading && bookings.length === 0 ? (
          <div className="space-y-4">
            <EmptyState
              icon={Calendar}
              title="No Bookings"
              description={`No bookings scheduled for ${data?.date_display || 'this day'}`}
            />
            {nextBookingDate && (
              <div className="flex justify-center">
                <Button onClick={jumpToNextBooking} className="bg-blue-600 hover:bg-blue-700 text-white h-10 gap-2">
                  Jump to Next Booking
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {/* Summary */}
            <div className="text-sm text-gray-500">
              {bookings.length} booking{bookings.length !== 1 ? 's' : ''} scheduled
            </div>
            
            {/* Hour Groups */}
            {hourSlots.map((hourSlot, slotIndex) => {
              const hourPassed = isHourPassed(hourSlot);
              const slotBookings = groupedBookings[hourSlot];
              const slotColor = SLOT_COLORS[slotIndex % SLOT_COLORS.length];
              
              return (
                <div key={hourSlot} className="space-y-4">
                  {/* Hour Header - Centered text on line */}
                  <div className="flex items-center gap-4">
                    <div className="h-px flex-1 bg-gray-200" />
                    <span className="text-sm text-gray-400 font-medium">
                      {hourSlot} · {slotBookings.length} trip{slotBookings.length !== 1 ? 's' : ''}
                    </span>
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>
                  
                  {/* Bookings */}
                  <div className="space-y-4">
                    {slotBookings.map((booking) => (
                      <BookingCard
                        key={booking.id}
                        booking={booking}
                        slotColor={slotColor}
                        onContact={handleContact}
                        onEdit={handleEdit}
                        showEditButton={true}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </TabPageLayout>
  );
};

export default ScheduleTab;
